import { toast } from '@askable/ui/core/sonner';
import { Box, MessageStar, PlayList, Survey, User, Website } from '@askable/ui/illustrations';
import { useFeatureFlags } from 'feature-flags';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'src/lib/routing';
import { match } from 'ts-pattern';
import { useBillingIsTracks } from 'src/hooks/useBillingIsTracks';
import { useCreateStudy } from 'src/hooks/useCreateStudy';
import { useIsAiModeratedStudyEnabled } from 'src/hooks/useIsAiModeratedStudyEnabled';
import { useIsSelfServeStudiesLocked } from 'src/hooks/useIsSelfServeStudiesLocked';

import { useConnectedClient } from 'context/ConnectedClientContext';

import type { StudyOption } from './CreateStudyItem';
import type { SetupFormData } from 'components/AIModeratedSetup';
import type { Device, LanguageLocale } from 'containers/Studies/types/recruit';

export const EXTERNAL_PROJECT_CREATION_URL = 'https://forms.monday.com/forms/889023410e57dd72375ab90c4c92a07e?r=apse2';

export type PopoverSide = 'right' | 'left';

type UseCreateStudyOptionsParams = {
  popoverSide: PopoverSide;
  projectId?: string;
  isResearcher: boolean;
  isGuest: boolean;
};

type ResearchType = 'AI_MODERATED' | 'UNMODERATED' | 'SURVEY' | 'INTERVIEW' | 'VIDEO_CALL' | 'PARTICIPANT_RECRUITMENT';

const interviewCreateHref = (id?: string) =>
  id
    ? `/booking-setup/create?research-type=remote-call&project_id=${id}`
    : '/booking-setup/create?research-type=remote-call';

const recruitCreateHref = (id?: string) =>
  id
    ? `/booking-setup/create?research-type=online-task&project_id=${id}`
    : '/booking-setup/create?research-type=online-task';

export const useCreateStudyOptions = ({
  popoverSide,
  projectId,
  isResearcher,
  isGuest,
}: UseCreateStudyOptionsParams) => {
  const { t } = useTranslation();
  const { t: tc } = useTranslation('components');
  const navigate = useNavigate();
  const viewer = useConnectedClient();
  const { enabled: isBillingTracksEnabled } = useBillingIsTracks();
  const { handleCreateUnmoderated, handleCreateAIModerated, handleCreateSurvey, handleCreateVideoCall } =
    useCreateStudy();
  const { moderated_scheduling_enabled } = useFeatureFlags(['moderated_scheduling_enabled']);
  const isAiModeratedStudyEnabled = useIsAiModeratedStudyEnabled();
  const isSelfServeStudiesLocked = useIsSelfServeStudiesLocked();

  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [setupOpenCount, setSetupOpenCount] = useState(0);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [pickedProjectId, setPickedProjectId] = useState<string | undefined>();
  const [pendingResearchType, setPendingResearchType] = useState<ResearchType | null>(null);

  // A researcher with no project must pick an Askable Plus project first. Other users skip the picker:
  // it shows only Askable Plus projects, so it is empty for them.
  const requiresProjectGate = isResearcher && !projectId;

  // The picker opens only when there is no `projectId`, so the two cannot conflict.
  const selectedProjectIdForSetup = projectId ?? pickedProjectId;

  const dispatchResearchType = useCallback(
    (type: ResearchType, id?: string) => {
      match(type)
        .with('AI_MODERATED', () => {
          setIsSetupOpen(true);
          setSetupOpenCount(count => count + 1);
        })
        .with('UNMODERATED', () => void handleCreateUnmoderated(id))
        .with('SURVEY', () => void handleCreateSurvey(id))
        .with('INTERVIEW', () => navigate(interviewCreateHref(id)))
        .with('VIDEO_CALL', () => void handleCreateVideoCall(id))
        .with('PARTICIPANT_RECRUITMENT', () => navigate(recruitCreateHref(id)))
        .exhaustive();
    },
    [navigate, handleCreateUnmoderated, handleCreateSurvey, handleCreateVideoCall],
  );

  const handleClick = useCallback(
    (type: ResearchType) => {
      if (requiresProjectGate) {
        setPendingResearchType(type);
        setIsProjectDialogOpen(true);
        return;
      }
      dispatchResearchType(type, projectId);
    },
    [requiresProjectGate, projectId, dispatchResearchType],
  );

  const handleProjectSelect = (selectedProjectId: string) => {
    setPickedProjectId(selectedProjectId);
    setIsProjectDialogOpen(false);
    if (pendingResearchType) {
      dispatchResearchType(pendingResearchType, selectedProjectId);
    }
    setPendingResearchType(null);
  };

  const handleSetupComplete = (data: SetupFormData) => {
    const context = {
      companyContext: data.companyContext,
      productContext: data.productContext,
      audienceContext: data.audienceContext,
      interviewContext: data.interviewContext,
    };

    const researchGoals = data.researchGoals.map(goal => goal.value);

    void handleCreateAIModerated({
      studyName: data.studyTitle,
      device: data.deviceRequirements,
      interviewLanguages: data.interviewLanguages,
      context,
      researchGoals,
      projectId: selectedProjectIdForSetup,
    });

    setIsSetupOpen(false);
  };

  const handleCustomSetup = ({
    studyName,
    device,
    interviewLanguages,
  }: {
    studyName: string;
    device: Device;
    interviewLanguages: LanguageLocale[];
  }) => {
    void handleCreateAIModerated({
      studyName,
      device,
      interviewLanguages,
      projectId: selectedProjectIdForSetup,
    });
    setIsSetupOpen(false);
  };

  const handleSetupClose = () => {
    setIsSetupOpen(false);
    if (isResearcher && !projectId) {
      setPickedProjectId(undefined);
    }
  };

  const handleSetupError = () => {
    handleSetupClose();
    toast.error(t('errors.somethingWentWrong'), {
      id: 'ai-moderated-setup-error',
      description: tc('aiModerated.genericToastError.description'),
      action: { label: t('global.refresh'), onClick: () => window.location.reload() },
    });
  };

  const studyOptions = useMemo<StudyOption[]>(() => {
    // Check the team lock before Tracks: a locked team cannot upgrade. The lock is per team, so it
    // applies to researchers too.
    const selfServeStatus = (() => {
      if (isSelfServeStudiesLocked) {
        return 'team_locked' as const;
      }
      return !isResearcher && !isBillingTracksEnabled ? ('upgrade_required' as const) : undefined;
    })();

    const options: StudyOption[] = [
      {
        title: t('sections.createStudy.studies.interview.title'),
        description: t('sections.createStudy.studies.interview.description'),
        icon: PlayList,
        ...(requiresProjectGate
          ? { onClick: () => handleClick('INTERVIEW') }
          : { href: interviewCreateHref(projectId) }),
      },
      // Tracks teams only: the studies builder has no credits checkout.
      ...(moderated_scheduling_enabled && isBillingTracksEnabled
        ? [
            {
              title: t('sections.createStudy.studies.videoCall.title'),
              description: t('sections.createStudy.studies.videoCall.description'),
              onClick: () => handleClick('VIDEO_CALL'),
              icon: PlayList,
              side: popoverSide,
            },
          ]
        : []),
      {
        title: t('sections.createStudy.studies.aiModerated.title'),
        description: t('sections.createStudy.studies.aiModerated.description'),
        onClick: () => handleClick('AI_MODERATED'),
        icon: MessageStar,
        status: !isResearcher && !isAiModeratedStudyEnabled ? 'upgrade_required' : undefined,
        side: popoverSide,
      },
      {
        title: t('sections.createStudy.studies.unmoderated.title'),
        description: t('sections.createStudy.studies.unmoderated.description'),
        onClick: () => handleClick('UNMODERATED'),
        icon: Website,
        status: selfServeStatus,
        side: popoverSide,
      },
      {
        title: t('sections.createStudy.studies.survey.title'),
        description: t('sections.createStudy.studies.survey.description'),
        onClick: () => handleClick('SURVEY'),
        icon: Survey,
        status: selfServeStatus,
        side: popoverSide,
      },
    ];

    options.push({
      title: t('sections.createStudy.studies.participantRecruitment.title'),
      description: t('sections.createStudy.studies.participantRecruitment.description'),
      icon: User,
      ...(requiresProjectGate
        ? { onClick: () => handleClick('PARTICIPANT_RECRUITMENT') }
        : { href: recruitCreateHref(projectId) }),
    });

    if (isGuest) {
      return options.map(option => ({ ...option, status: 'guest_locked' as const, side: popoverSide }));
    }

    return options;
  }, [
    t,
    isResearcher,
    isBillingTracksEnabled,
    isSelfServeStudiesLocked,
    isAiModeratedStudyEnabled,
    moderated_scheduling_enabled,
    popoverSide,
    requiresProjectGate,
    projectId,
    handleClick,
    isGuest,
  ]);

  const projectOptions = useMemo<StudyOption[]>(
    () => [
      {
        title: t('sections.createStudy.project.projectDelivery.title'),
        description: t('sections.createStudy.project.projectDelivery.description'),
        href:
          viewer.isAdminDelegate || !isBillingTracksEnabled ? '/askable-plus/create' : EXTERNAL_PROJECT_CREATION_URL,
        newTab: !viewer.isAdminDelegate && isBillingTracksEnabled,
        icon: Box,
        ...(isGuest ? { status: 'guest_locked' as const, side: popoverSide } : {}),
      },
    ],
    [t, isBillingTracksEnabled, viewer.isAdminDelegate, isGuest, popoverSide],
  );

  return {
    studyOptions,
    projectOptions,
    isSetupOpen,
    setIsSetupOpen,
    setupOpenCount,
    handleSetupComplete,
    handleCustomSetup,
    handleSetupClose,
    handleSetupError,
    isProjectDialogOpen,
    setIsProjectDialogOpen,
    handleProjectSelect,
  };
};
